import { Component } from '@theme/component';

const MAX_SVG_BYTES = 5_000_000;

class CustomDecorativeAsset extends Component {
  /** @type {Map<string, Promise<string>>} */
  static cache = new Map();
  static observedAttributes = ['data-src', 'data-paint', 'data-sizing'];

  #loadVersion = 0;

  connectedCallback() {
    super.connectedCallback();
    this.load();
  }

  updatedCallback() {
    super.updatedCallback();
    this.load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#loadVersion += 1;
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected) this.load();
  }

  async load() {
    const loadVersion = ++this.#loadVersion;
    const src = this.dataset.src;
    const paint = this.dataset.paint || 'original';
    const target = this.querySelector('.custom-decorative-asset__drawing');

    if (!src || !(target instanceof HTMLElement)) {
      this.dataset.status = 'empty';
      return;
    }

    if (!paint.startsWith('svg')) {
      target.querySelector('.custom-decorative-asset__inline-svg')?.remove();
      // Let CSS request image and mask sources so hidden decorative assets do not load unnecessarily.
      this.dataset.status = 'ready';
      return;
    }

    const currentSvg = target.querySelector('.custom-decorative-asset__inline-svg');
    if (currentSvg && !(currentSvg instanceof SVGElement)) return;
    if (currentSvg?.dataset.source === src) {
      this.dataset.status = 'ready';
      return;
    }

    currentSvg?.remove();
    this.dataset.status = 'loading';

    try {
      const svgText = await CustomDecorativeAsset.getSvg(src);
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, 'image/svg+xml');
      const svg = doc.documentElement;

      if (doc.querySelector('parsererror') || svg.nodeName.toLowerCase() !== 'svg') {
        throw new Error('Decorative asset did not parse as SVG.');
      }

      CustomDecorativeAsset.sanitize(svg);
      CustomDecorativeAsset.applySizing(svg, this.dataset.sizing || 'contain');
      svg.classList.add('custom-decorative-asset__inline-svg');
      svg.dataset.source = src;
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');

      if (loadVersion !== this.#loadVersion || !this.isConnected) return;
      target.replaceChildren(document.importNode(svg, true));
      this.dataset.status = 'ready';
    } catch (error) {
      if (loadVersion !== this.#loadVersion || !this.isConnected) return;
      this.dataset.status = 'error';
      console.error('[custom-decorative-asset] Unable to render SVG.', error);
    }
  }

  /** @param {Element} svg */
  static sanitize(svg) {
    svg.querySelectorAll('script, style, foreignObject, iframe, object, embed').forEach((node) => node.remove());

    svg.querySelectorAll('*').forEach((node) => {
      [...node.attributes].forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        const value = attribute.value.trim();
        const normalizedValue = value.toLowerCase();

        if (
          name.startsWith('on') ||
          normalizedValue.startsWith('javascript:') ||
          normalizedValue.startsWith('data:text/html') ||
          (name === 'style' && /(?:url|expression)\s*\(/i.test(value)) ||
          ((name === 'href' || name === 'xlink:href') && value !== '' && !value.startsWith('#'))
        ) {
          node.removeAttribute(attribute.name);
        }
      });
    });
  }

  /**
   * @param {Element} svg
   * @param {string} sizing
   */
  static applySizing(svg, sizing) {
    if (sizing === 'cover') {
      svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    } else if (sizing === 'fill') {
      svg.setAttribute('preserveAspectRatio', 'none');
    } else {
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    }
  }

  /** @param {string} src */
  static async getSvg(src) {
    const url = new URL(src, document.baseURI);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      throw new Error('Decorative asset SVG URL must use HTTP or HTTPS.');
    }

    if (!CustomDecorativeAsset.cache.has(url.href)) {
      const request = fetch(url.href, { credentials: 'same-origin' })
        .then(async (response) => {
          if (!response.ok) throw new Error(`Decorative asset request failed: ${response.status}`);

          const contentLength = Number(response.headers.get('content-length'));
          if (contentLength > MAX_SVG_BYTES) throw new Error('Decorative asset SVG is too large.');

          const svgText = await response.text();
          if (svgText.length > MAX_SVG_BYTES) throw new Error('Decorative asset SVG is too large.');
          return svgText;
        })
        .catch((error) => {
          CustomDecorativeAsset.cache.delete(url.href);
          throw error;
        });

      CustomDecorativeAsset.cache.set(url.href, request);
    }

    const cachedRequest = CustomDecorativeAsset.cache.get(url.href);
    if (!cachedRequest) throw new Error('Decorative asset SVG request was not cached.');
    return cachedRequest;
  }
}

if (!customElements.get('custom-decorative-asset')) {
  customElements.define('custom-decorative-asset', CustomDecorativeAsset);
}
